import React, { useState, useContext } from "react";
import {
  Button,
  Box,
  Typography,
  TextField,
  Stack,
  IconButton,
  Paper,
  Switch,
} from "@mui/material";
import { PhotoCamera, CloudUpload } from "@mui/icons-material";
import axios from "axios";
import SlidingSidebar, {
  ButtonControls,
} from "../../../Components/SlidingSidebar";
import Avatar from "../../../Components/Avatar";
import UserContext from "../../../UserContext";

interface AddBrandProps {
  onBrandAdded?: () => void;
}

interface BrandFormData {
  brand_name: string;
  username: string;
  avatar_url: string;
}

const AddBrand: React.FC<AddBrandProps> = ({ onBrandAdded }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<BrandFormData>({
    brand_name: "",
    username: "",
    avatar_url: "",
  });
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const { selectedSupplierId } = useContext(UserContext) || {};
  const [isVerified, setIsVerified] = useState(true);
  const [isActive, setIsActive] = useState(true);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormData({
      brand_name: "",
      username: "",
      avatar_url: "",
    });
    setPreviewUrl("");
  };

  const handleInputChange = (field: keyof BrandFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAvatarChange = async (file: File) => {
    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Create form data for upload
    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/brands/brand/upload-avatar`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      handleInputChange("avatar_url", response.data.avatar_url);
    } catch (error) {
      console.error("Error uploading avatar:", error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedSupplierId) return;

    try {
      // Create brand in brand_brands table with default status
      const brandResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}/brands/brand`,
        {
          brand: formData.brand_name,
          avatar_url: formData.avatar_url,
          supplier_id: selectedSupplierId,
          brand_status: {
            Active: "Yes",
            Verified: "No",
          },
        }
      );

      // Create username in profile_profiles table
      if (brandResponse.data.id) {
        await axios.post(`${import.meta.env.VITE_API_URL}/profiles/create`, {
          username: formData.username,
          brand_id: brandResponse.data.id,
          type: "Brand",
        });
      }

      handleClose();
      if (onBrandAdded) {
        onBrandAdded();
      }
    } catch (error) {
      console.error("Error creating brand:", error);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<CloudUpload />}
      >
        Add Brand
      </Button>

      <SlidingSidebar open={open} onClose={handleClose} width="500px">
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
            <Stack spacing={0.5}>
              <Paper elevation={0} sx={{ p: 1 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Brand Information</Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      src={previewUrl || "/default-avatar.png"}
                      alt="Brand avatar"
                      size={80}
                    />
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="avatar-upload"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleAvatarChange(file);
                        }
                      }}
                    />
                    <label htmlFor="avatar-upload">
                      <IconButton component="span">
                        <PhotoCamera />
                      </IconButton>
                    </label>
                  </Box>

                  <TextField
                    label="Brand Name"
                    value={formData.brand_name}
                    onChange={(e) =>
                      handleInputChange("brand_name", e.target.value)
                    }
                    size="small"
                    fullWidth
                    required
                  />

                  <TextField
                    label="Username"
                    value={formData.username}
                    onChange={(e) =>
                      handleInputChange("username", e.target.value)
                    }
                    size="small"
                    fullWidth
                    required
                  />
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ p: 1 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Status</Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography>Verified</Typography>
                    <Switch
                      checked={isVerified}
                      onChange={() => setIsVerified((prev) => !prev)}
                      color="primary"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography>Active</Typography>
                    <Switch
                      checked={isActive}
                      onChange={() => setIsActive((prev) => !prev)}
                      color="primary"
                    />
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Box>

          <ButtonControls>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Create Brand
            </Button>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
          </ButtonControls>
        </Box>
      </SlidingSidebar>
    </>
  );
};

export default AddBrand;
